// src/pages/appointments/past/PastAppointments.jsx
import React from "react";
import { useNavigate } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Card from "@/components/ui/Card.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import FullBleedDivider from "@/sot/primitives/FullBleedDivider.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import useMyPastAppointments from "@/hooks/useMyPastAppointments.js";
import useMyUpcomingAppointments from "@/hooks/useMyUpcomingAppointments.js";
import {
  listMyAppointmentDoctorReviews,
  subscribeAppointmentDoctorReviews,
} from "@/services/appointmentDoctorReviews.js";
import {
  buildFavoriteDoctorIds,
  isFavoriteDoctor,
  subscribeDoctorFavorites,
} from "@/services/doctorFavorites.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";
import { formatYearsExperienceLabel } from "@/utils/experience.js";

const ICON_SEARCH = "/icons/doctor/search.svg";
const ICON_CANCEL = "/icons/doctor/cancel.svg";

const ICON_YEARS = "/icons/appointments/years_experience.svg"; // 18x18
const ICON_STAR = "/icons/appointments/star.svg"; // 24x24
const ICON_VERIFIED = "/icons/doctor/verified.svg";
const ICON_PENDING_VERIFICATION = "/icons/pending-verification.svg";

function safeDate(dateISO) {
  const raw = String(dateISO || "").trim();
  if (!raw) return null;
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? new Date(`${raw}T00:00:00`)
    : new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatPastLine(dateISO, timeHHMM, formatDate, t) {
  const d = safeDate(dateISO);
  if (!d) return t("appointments.past.common.unknownDate", "—");
  const weekday = formatDate(d, { weekday: "long" });
  const day = formatDate(d, { day: "2-digit" });
  const month = formatDate(d, { month: "short" });
  return `${weekday} ${day} ${month} ${timeHHMM || ""}`.trim();
}

function monthKey(dateISO) {
  const date = safeDate(dateISO);
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key, formatDate, t) {
  const [y, m] = key.split("-");
  const idx = Number(m) - 1;
  const date = new Date(Number(y), idx >= 0 ? idx : 0, 1);
  if (Number.isNaN(date.getTime())) {
    return t("appointments.past.common.monthFallback", "Month {year}", { year: y || "" }).trim();
  }
  return formatDate(date, { month: "long", year: "numeric" });
}

function groupByMonth(rows, formatDate, t) {
  const map = new Map();
  rows.forEach((r) => {
    const k = monthKey(r.dateISO);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  });

  // newest month first; newest appointment first within month
  const keys = Array.from(map.keys()).sort((a, b) => (a < b ? 1 : -1));

  return keys.map((k) => ({
    key: k,
    label: monthLabel(k, formatDate, t),
    items: map
      .get(k)
      .slice()
      .sort((a, b) => {
        const aKey = `${a.dateISO || ""} ${a.time || ""}`;
        const bKey = `${b.dateISO || ""} ${b.time || ""}`;
        return aKey < bKey ? 1 : -1;
      }),
  }));
}

export default function PastAppointments() {
  const nav = useNavigate();
  const { t, formatDate } = useLanguage();
  const { loading, error, items } = useMyPastAppointments();
  const { items: upcomingItems } = useMyUpcomingAppointments();
  const [reviewMap, setReviewMap] = React.useState(() => new Map());
  const [, setFavoriteVersion] = React.useState(0);

  React.useEffect(
    () => subscribeDoctorFavorites(() => setFavoriteVersion((value) => value + 1)),
    []
  );

  React.useEffect(() => {
    let alive = true;

    async function hydrateReviews() {
      const appointmentIds = (items || [])
        .map((item) => String(item?.id || "").trim())
        .filter(Boolean);

      if (appointmentIds.length === 0) {
        if (alive) setReviewMap(new Map());
        return;
      }

      try {
        const nextReviewMap = await listMyAppointmentDoctorReviews(appointmentIds);
        if (alive) setReviewMap(nextReviewMap);
      } catch {
        if (alive) setReviewMap(new Map());
      }
    }

    hydrateReviews();
    const unsubscribe = subscribeAppointmentDoctorReviews(hydrateReviews);

    return () => {
      alive = false;
      unsubscribe?.();
    };
  }, [items]);

  const rows = React.useMemo(
    () =>
      (items || []).map((item) => ({
        id: item.id,
        doctor: item.doctor,
        specialty:
          item.doctor?.specialty || t("appointments.past.common.doctorFallback", "Doctor"),
        years: item.doctor?.yearsExperience || t("appointments.past.common.unknownValue", "—"),
        rating: item.doctor?.rating || t("appointments.past.common.unknownValue", "—"),
        dateISO: item.selectedDate || item.when?.dateISO || "",
        time: item.selectedTime || item.when?.time || "",
        status: String(item?.status || "").trim().toLowerCase(),
        review: reviewMap.get(String(item?.id || "").trim()) || null,
      })),
    [items, reviewMap, t]
  );

  const [query, setQuery] = React.useState("");

  const filtered = React.useMemo(() => {
    const q = String(query || "").trim().toLowerCase();

    // This screen is Past-only for now. Tabs are blueprint-only.
    const base = rows;

    if (!q) return base;
    return base.filter((r) => {
      const hay = `${resolvePatientFacingDoctorName(r.doctor, r.doctor?.name)} ${r.specialty || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  const groups = React.useMemo(
    () => groupByMonth(filtered, formatDate, t),
    [filtered, formatDate, t]
  );

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail">
        <TopBarCenter
          title={t("appointments.past.list.title", "Past appointments")}
          onBack={() => nav(-1)}
        />

        {/* Ghost search */}
        <div className="mt-2">
          <div className="flex items-center gap-3 h-12 rounded-full bg-transparent px-1">
            <img src={ICON_SEARCH} alt="" className="h-6 w-6 opacity-70" draggable="false" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("appointments.past.list.searchPlaceholder", "Search")}
              className="
                flex-1 bg-transparent outline-none
                text-body-large text-on-surface
                placeholder:text-on-surface-var
              "
            />
            {query ? (
              <button
                type="button"
                aria-label={t("appointments.past.list.clearSearch", "Clear")}
                className="h-10 w-10 grid place-items-center rounded-[12px]"
                onClick={() => setQuery("")}
              >
                <img src={ICON_CANCEL} alt="" className="h-6 w-6 opacity-70" draggable="false" />
              </button>
            ) : null}
          </div>

          {/* Pills row (Appointments variant) */}
          <div className="mt-4 flex gap-3 overflow-x-auto no-scrollbar pb-1">
            <AfriPill
                variant="appointments"
                size="medium"
                value="upcoming"
                label={t("appointments.past.list.tabs.upcoming", "Upcoming ({count})", {
                  count: upcomingItems?.length || 0,
                })}
                isActive={false}
                onChange={() => nav("/appointments?tab=upcoming")}
            />
            <AfriPill
                variant="appointments"
                size="medium"
                value="past"
                label={t("appointments.past.list.tabs.past", "Past ({count})", {
                  count: rows.length,
                })}
                isActive={true}
                onChange={() => {}}
            />
            <AfriPill
                variant="appointments"
                size="medium"
                value="pending"
                label={t(
                  "appointments.past.list.tabs.pending",
                  "Pending for approval ({count})",
                  { count: 0 }
                )}
                isActive={false}
                onChange={() => nav("/appointments?tab=pending")}
            />
            </div>

          {/* Divider edge-edge under Search */}
          <FullBleedDivider className="my-4" />
          <div className="h-5" />
        </div>

        {/* Monthly groups */}
        <div className="space-y-10">
          {loading ? (
            <div className="text-body-medium text-on-surface-var">
              {t("appointments.past.list.loading", "Loading...")}
            </div>
          ) : null}

          {!loading && error ? (
            <div className="text-body-medium text-[crimson]">{error}</div>
          ) : null}

          {groups.map((g, groupIdx) => (
            <div key={g.key}>
              {/* Month label (centered like blueprint) */}
              <div className="text-title-large text-on-surface text-center mb-6">{g.label}</div>

              {/* Month list */}
              <div className="space-y-4">
                {g.items.map((r) => {
                  const favoriteIds = buildFavoriteDoctorIds(r.doctor);
                  const isFavorite = isFavoriteDoctor(favoriteIds);
                  const verificationStatus = normalizeVerificationStatus(
                    r.doctor?.verificationStatus || r.doctor?.verified_status,
                    r.doctor?.verified ? "verified" : ""
                  );
                  const showVerifiedBadge =
                    verificationStatus === "verified" || verificationStatus === "pending";

                  return (
                  <div key={r.id}>
                    {/* Appointment date line (top-left, outside card) */}
                    <div className="text-title-medium text-on-surface-var mb-2">
                      {formatPastLine(r.dateISO, r.time, formatDate, t)}
                    </div>

                    {/* Doctor row card */}
                    <Card
                      as="button"
                      type="button"
                      className="
                        w-full text-left
                        overflow-hidden
                      "
                      onClick={() => nav(`/appointments/past/${r.id}`)}
                    >
                      <div className="px-4 py-4 flex items-center gap-3">
                        {/* Avatar 68x68 (square, framed) */}
                        <AvatarThumb68
                          src={r.doctor?.avatar || "/images/demo/doctors/avatar-fallback.jpg"}
                          alt={resolvePatientFacingDoctorName(r.doctor, r.doctor?.name)}
                        />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <div className="text-title-medium text-on-surface truncate">
                                  {resolvePatientFacingDoctorName(r.doctor, r.doctor?.name)}
                                </div>
                                {showVerifiedBadge ? (
                                  <span className="inline-flex h-5 w-5 shrink-0">
                                    <img
                                      src={
                                        verificationStatus === "verified"
                                          ? ICON_VERIFIED
                                          : ICON_PENDING_VERIFICATION
                                      }
                                      alt=""
                                      className="h-full w-full object-contain"
                                      draggable="false"
                                    />
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-body-medium text-on-surface-var mt-0.5">
                                {r.specialty}
                              </div>

                              {/* Years + rating row */}
                              <div className="mt-1 flex items-center gap-3 text-body-medium text-on-surface-var">
                                <div className="flex items-center gap-1.5">
                                  <span>{formatYearsExperienceLabel(r.years, t)}</span>
                                  <img
                                    src={ICON_YEARS}
                                    alt=""
                                    className="h-[18px] w-[18px]"
                                    draggable="false"
                                  />
                                </div>

                                <div className="flex items-center gap-1.5">
                                  <span>{r.rating}</span>
                                  <img
                                    src={ICON_STAR}
                                    alt=""
                                    className="h-[16px] w-[16px] shrink-0 translate-y-[1px]"
                                    draggable="false"
                                  />
                                </div>
                              </div>

                              {r.status === "completed" ? (
                                <div
                                  className={`
                                    mt-2 inline-flex items-center rounded-full px-3 py-1 text-label-medium
                                    ${r.review
                                      ? "bg-[var(--state-primary-12)] text-primary"
                                      : "bg-[var(--state-primary-12)] text-primary"}
                                  `}
                                >
                                  {r.review
                                    ? t(
                                        "appointments.past.list.reviewSubmitted",
                                        "Review submitted"
                                      )
                                    : t(
                                        "appointments.past.list.reviewPending",
                                        "Rate doctor and experience"
                                      )}
                                </div>
                              ) : null}
                            </div>

                            {/* Favourite (right aligned) */}
                            <div className="shrink-0 pt-0.5">
                              <div className="h-11 w-11 grid place-items-center">
                                <img
                                  src={
                                    isFavorite
                                      ? "/icons/patient/favorite.svg"
                                      : "/icons/patient/favorite_outline.svg"
                                  }
                                  alt=""
                                  className={
                                    isFavorite
                                      ? "h-6 w-6 opacity-100"
                                      : "h-6 w-6 opacity-70"
                                  }
                                  draggable="false"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                  );
                })}
              </div>

              {/* Month separator (centered, capped width) */}
                {groupIdx !== groups.length - 1 ? (
                <div className="mt-10 mb-2">
                    <FullBleedDivider />
                </div>
                ) : null}
            </div>
          ))}

          {!loading && !error && groups.length === 0 ? (
            <div className="text-body-medium text-on-surface-var">
              {t("appointments.past.list.empty", "No past appointments found.")}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
